import { CartItemInterface } from '@/models/cart-item'
import {
  computeLineTotalForCartItemBase,
} from '@/domain/cart/cart.pricing'
import type Order from '@/models/order'
import type { orderItemList } from '@/models/order'
import { OrderStatus, parseOrderStatus } from '@/types/enums/OrderStatus'
import type { CartLine, CartStoreSnapshot } from '@/store/cart/cart.types'
import { cartLineToCartItemInterface } from '@/store/cart/cart-line-adapters'

/**
 * Cart Utility Functions
 *
 * Implements cart clearing and restoration logic following strict rules:
 * - Do NOT clear cart when order is created
 * - Do NOT clear cart when payment starts
 * - Do NOT clear cart when payment fails
 * - Clear cart ONLY when payment succeeds
 * - On order cancellation or expiry, restore cart from order
 */

/**
 * Checks if cart should be cleared based on order status.
 * Cart should ONLY be cleared when payment succeeds (order status is Confirmed or later).
 *
 * @param orderStatus - Current order status (API value: number or string)
 * @returns true if cart should be cleared, false otherwise
 */
export function shouldClearCartOnPaymentSuccess(orderStatus: unknown): boolean {
  const status = parseOrderStatus(orderStatus)
  return status >= OrderStatus.Confirmed
}

/**
 * Checks if cart should be restored from order.
 * Cart should be restored when order is cancelled.
 *
 * @param orderStatus - Current order status (API value: number or string)
 * @returns true if cart should be restored, false otherwise
 */
export function shouldRestoreCartFromOrder(orderStatus: unknown): boolean {
  const status = parseOrderStatus(orderStatus)
  return status === OrderStatus.Cancelled
}

/**
 * Calculates line total for a cart item including addons.
 * Formula: count × (item_price + addonsTotal).
 * addonsTotal is per-unit; quantity multiplies both base and addon prices.
 *
 * @param item - Cart item
 * @returns Line total
 */
function toNum(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

export function getCartItemLineTotal(item: CartItemInterface | null | undefined): number {
  if (!item || (item?.count ?? 0) < 1) return 0
  const base = computeLineTotalForCartItemBase(item.item_price, item.count, {
    selectedModifiers: item.selectedModifiers,
  })
  return base + toNum(item.comboTotalPerUnit) * Math.max(0, Math.floor(toNum(item.count)))
}

/** Prefer itemTotal from store; fallback to computed for backward compat. Safe: avoids NaN, uses Number(value ?? 0). */
export function getItemTotal(item: CartLine | CartItemInterface | null | undefined): number {
  if (!item) return 0
  const stored = Number(item.itemTotal ?? 0)
  if (Number.isFinite(stored) && stored >= 0) return stored
  const legacy =
    "selected_modifiers" in item ? cartLineToCartItemInterface(item) : item
  const computed = getCartItemLineTotal(legacy)
  return Number.isFinite(computed) ? computed : 0
}

/**
 * Converts order items to cart items.
 * Maps orderItemList from Order to CartItemInterface format.
 *
 * @param orderItems - Array of order items from Order.orderItemList
 * @param restaurantId - Restaurant ID to associate with cart items
 * @returns Array of cart items ready to be added to cart
 */
export function convertOrderItemsToCartItems(
  orderItems: orderItemList[],
  restaurantId: string
): CartItemInterface[] {
  if (!orderItems || orderItems.length === 0) {
    return []
  }

  return orderItems.map((item) => ({
    menu_item_id: item.menu_item_id,
    item_name: item.item_name,
    item_price: item.item_price,
    count: item.quantity,
    category: item.category,
    restaurant_id: restaurantId,
  }))
}

/**
 * Restores cart from order.
 * Used when order is cancelled to restore items back to cart.
 *
 * @param order - Order object containing orderItemList
 * @param currentCart - Current cart state (optional, for validation)
 * @returns Cart items restored from order, or empty array if order is invalid
 */
export function restoreCartFromOrder(
  order: Order | null | undefined,
  _currentCart?: CartStoreSnapshot
): CartItemInterface[] {
  // Validate order exists and has items
  if (!order || !order.orderItemList || order.orderItemList.length === 0) {
    return []
  }

  // Validate order status allows restoration
  if (!shouldRestoreCartFromOrder(order.order_status)) {
    return []
  }

  // Convert order items to cart items
  const restoredItems = convertOrderItemsToCartItems(
    order.orderItemList,
    order.restaurant_id
  )

  return restoredItems
}

/**
 * Validates if cart can be cleared.
 * Ensures cart is only cleared on payment success, not on other events.
 *
 * @param orderStatus - Order status to validate (API value)
 * @param isPaymentSuccess - Explicit payment success flag (optional)
 * @returns true if cart clearing is allowed, false otherwise
 */
export function canClearCart(
  orderStatus: unknown,
  isPaymentSuccess?: boolean
): boolean {
  // Explicit payment success flag takes precedence
  if (isPaymentSuccess === true) {
    return shouldClearCartOnPaymentSuccess(orderStatus)
  }

  // If payment success is explicitly false, do not clear
  if (isPaymentSuccess === false) {
    return false
  }

  // Otherwise, check order status
  return shouldClearCartOnPaymentSuccess(orderStatus)
}

/**
 * Validates if cart should NOT be cleared.
 * Returns true if cart should be preserved (order created, payment pending, payment failed, etc.)
 *
 * @param orderStatus - Order status to validate (API value)
 * @param isPaymentSuccess - Explicit payment success flag (optional)
 * @returns true if cart should be preserved, false otherwise
 */
export function shouldPreserveCart(
  orderStatus: unknown,
  isPaymentSuccess?: boolean
): boolean {
  return !canClearCart(orderStatus, isPaymentSuccess)
}

/**
 * Gets cart restoration reason for logging/debugging.
 *
 * @param orderStatus - Order status (API value)
 * @returns Human-readable reason for restoration, or null if not applicable
 */
export function getCartRestorationReason(
  orderStatus: unknown
): string | null {
  if (!shouldRestoreCartFromOrder(orderStatus)) {
    return null
  }

  const status = parseOrderStatus(orderStatus)

  if (status === OrderStatus.Cancelled) {
    return 'Order was cancelled'
  }

  return null
}

/**
 * Validates cart state before clearing.
 * Ensures cart is not empty and has valid items before clearing.
 *
 * @param cart - Current cart state
 * @returns true if cart is valid for clearing, false otherwise
 */
export function validateCartBeforeClearing(cart: Pick<CartStoreSnapshot, "items"> | null | undefined): boolean {
  if (!cart) return false
  if (!cart.items || cart.items.length === 0) return false
  return true
}

/**
 * Validates cart state before restoration.
 * Checks if restoration would conflict with existing cart items.
 *
 * @param cart - Current cart state
 * @param order - Order to restore from
 * @returns true if restoration is safe, false if there might be conflicts
 */
export function validateCartBeforeRestoration(
  cart: Pick<CartStoreSnapshot, "items"> | null | undefined,
  order: Order | null | undefined
): boolean {
  if (!order) return false

  // If cart is empty, restoration is always safe
  if (!cart || !cart.items || cart.items.length === 0) {
    return true
  }

  // If cart has items, check if they're from the same restaurant
  const cartRestaurantId = cart.items[0]?.restaurant_id
  return cartRestaurantId === order.restaurant_id || !cartRestaurantId
}
